/**
 * Checkout, in the page it was started from — ruling R-0825-2.
 *
 * WHAT THIS REPLACES. Every purchase used to end with `window.location.href =
 * checkout_url`: the app was torn down, a processor's page was loaded in its
 * place, and the way back was a query parameter (`?paid=1`) read on the next
 * boot. That cost a full reload each way, put two entries in the history so the
 * back button landed on a dead session, and lost every piece of unsaved state
 * the buyer had on screen. The redirect is retired, not deprecated — there is
 * no arm here that navigates.
 *
 * THE MODAL IS STATE, NOT NAVIGATION. Nothing here touches `history`,
 * `location` or the router. Opening it changes one module-level value; closing
 * it changes it back. So the back button after a checkout means what it meant
 * before the checkout, which is the whole of the requirement.
 *
 * TWO WAYS A CHECKOUT IS PUT ON SCREEN, and the server says which:
 *
 *   `page`     — a URL the modal loads in an IFRAME. FakeStripe answers this
 *                way and it is the only path the mock has, so the whole
 *                development and test flow runs with the network unplugged.
 *   `embedded` — Stripe's embedded checkout, mounted by Stripe.js. See
 *                `loadStripeJs` for the one third-party script this product
 *                loads and the terms it is loaded on.
 *
 * WHY A MODULE-LEVEL STORE RATHER THAN CONTEXT. Six surfaces start a checkout
 * and they do not share a provider: two live in the billing panel, two at a
 * stranger's door, one in the onboarding wizard and one in the owner's own
 * thread. A context that covered all six would have to be the router's root,
 * at which point it is a global with extra steps. This is the global, stated as
 * one, with `useSyncExternalStore` doing the subscription.
 */

/** What the server handed back, in the shape the modal renders from. */
export type CheckoutOpening =
  | { kind: "page"; url: string }
  | { kind: "embedded"; clientSecret: string; publishableKey: string };

/** How a checkout ended. The same two words the `?paid=` round trip used. */
export type CheckoutResult = "paid" | "unpaid";

export type CheckoutSession = {
  opening: CheckoutOpening;
  /**
   * Told once, when the modal closes, whatever closed it.
   *
   * A DISMISSAL IS `unpaid`, deliberately: it is what the processor's cancel
   * URL used to produce, and every surface already knows how to answer it
   * ("Nothing was charged."). A modal that reported nothing on dismissal would
   * leave a screen mid-purchase with no way to know the purchase had stopped.
   */
  onDone: (result: CheckoutResult) => void;
};

let current: CheckoutSession | null = null;
const listeners = new Set<() => void>();

/**
 * HOW MANY CHECKOUTS HAVE BEEN PAID FOR IN THIS SESSION.
 *
 * A counter and not a boolean, because what a reader wants to know is not "has
 * anybody ever paid" but "has anybody paid SINCE I LAST LOOKED" — a second
 * purchase has to be as visible as the first. It rides the same listener set
 * as the session itself, so `subscribeCheckout` covers both and there is one
 * store here rather than two.
 *
 * It exists because a purchase is an act that surfaces OUTSIDE the checkout
 * need to hear about: a knock refused for want of money is waiting on exactly
 * this event, and the modal's `onDone` is only told to the screen that opened
 * it (`compose-pane.tsx`, gauntlet walk 2026-09-13 round two, G-2b).
 */
let paid = 0;

function announce(): void {
  for (const listener of listeners) listener();
}

export function subscribeCheckout(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentCheckout(): CheckoutSession | null {
  return current;
}

/** The server render has no modal, and no store to read one from. */
export function noCheckout(): null {
  return null;
}

/** How many checkouts have ended `paid` in this session. */
export function checkoutsPaid(): number {
  return paid;
}

/** The server render has paid for nothing, and the number must be stable. */
export function noCheckoutsPaid(): number {
  return 0;
}

/**
 * Put a started checkout on screen.
 *
 * A second call while one is open REPLACES it and reports the first as unpaid,
 * rather than stacking two card forms or silently dropping the new one: two
 * checkouts at once is not a state this product has, and the surface that owns
 * the first one still has to be told it is over.
 */
export function openCheckout(session: CheckoutSession): void {
  const previous = current;
  current = session;
  announce();
  previous?.onDone("unpaid");
}

/**
 * Close it, telling whoever opened it how it ended. Idempotent.
 *
 * The paid count moves BEFORE the announcement, so a subscriber woken by the
 * modal's disappearance reads the outcome in the same pass rather than in the
 * next one.
 */
export function closeCheckout(result: CheckoutResult): void {
  const session = current;
  if (!session) return;
  current = null;
  if (result === "paid") paid += 1;
  announce();
  session.onDone(result);
}

/* ----------------------------------------------------- the FakeStripe bridge */

/**
 * The name FakeStripe's last page posts under (`apps/api/src/routes/devStripe.ts`
 * spells the same constant). A `message` listener hears every frame on the page,
 * so "is this ours" has to be answerable rather than inferred from shape.
 */
export const CHECKOUT_MESSAGE = "nob:checkout-complete";

/**
 * Read a `message` event as a checkout outcome, or not at all.
 *
 * THE ORIGIN IS CHECKED BY THE CALLER, not here, because only the caller knows
 * which origin it put in the iframe — under the dev proxy that is our own, and
 * against the api directly it is another port. Passing the expected origin in
 * keeps this a pure function and keeps the check impossible to forget: there is
 * no arm that accepts a message without one.
 */
export function readCheckoutMessage(
  event: MessageEvent,
  expectedOrigin: string,
): CheckoutResult | null {
  if (event.origin !== expectedOrigin) return null;
  const data = event.data as { type?: unknown; paid?: unknown } | null;
  if (!data || typeof data !== "object" || data.type !== CHECKOUT_MESSAGE) return null;
  return data.paid === true ? "paid" : "unpaid";
}

/* ------------------------------------------------------------ Stripe.js, lazily */

/**
 * THE ONE THIRD-PARTY SCRIPT IN THIS PRODUCT, and the terms it is here on.
 *
 * Ruling R-0825-2 names it explicitly: "Stripe.js on that one surface is the
 * documented third-party-script exception". Everything else in this app is
 * served from our own origin — no CDN, no font host, no analytics tag, no
 * avatar fetched from a stranger (`profile/avatar.ts`) — and that is not an
 * accident to be eroded one convenience at a time. So:
 *
 *  1. It is NOT A DEPENDENCY. There is no `@stripe/stripe-js` in the manifest
 *     and there must not be one. A package would be bundled, which would put a
 *     payment processor's code into the first byte of every visit including the
 *     ones that never buy anything.
 *  2. It is NOT A DYNAMIC `import()`. That is code splitting, which FR-1.2
 *     forbids and which `scripts/check-bundle-size.ts` is built around. What
 *     this does is append a `<script>` element — a DOM operation the bundler
 *     never sees, so the bundle stays one file and stays the size it is.
 *  3. It is loaded WHEN THE MODAL OPENS AND NOT BEFORE. A reader who never buys
 *     anything never contacts Stripe, which is the privacy half of the same
 *     rule (§6.5, "no third-party trackers anywhere").
 *  4. THE MOCK NEVER TOUCHES IT. FakeStripe is an iframe of our own HTML, so
 *     development, the test suites and e2e all run fully offline.
 *
 * Loaded once per document: the promise is the cache, so two checkouts in one
 * session append one tag.
 */
const STRIPE_JS = "https://js.stripe.com/v3/";

/** The sliver of Stripe.js this product uses. Typed here; nothing is imported. */
type EmbeddedCheckout = { mount: (target: HTMLElement) => void; destroy: () => void };
type StripeClient = {
  initEmbeddedCheckout: (options: {
    clientSecret: string;
    onComplete?: () => void;
  }) => Promise<EmbeddedCheckout>;
};
type StripeFactory = (publishableKey: string) => StripeClient;

let pending: Promise<StripeFactory> | null = null;

export function loadStripeJs(): Promise<StripeFactory> {
  if (pending) return pending;
  pending = new Promise<StripeFactory>((resolve, reject) => {
    const global = (window as unknown as { Stripe?: StripeFactory }).Stripe;
    if (global) {
      resolve(global);
      return;
    }
    const tag = document.createElement("script");
    tag.src = STRIPE_JS;
    tag.async = true;
    tag.addEventListener("load", () => {
      const loaded = (window as unknown as { Stripe?: StripeFactory }).Stripe;
      if (loaded) resolve(loaded);
      else reject(new Error("Stripe.js loaded without a Stripe global"));
    });
    tag.addEventListener("error", () => {
      // The promise is dropped so the next attempt tries again: a checkout
      // refused because a script failed once is not a checkout refused forever.
      pending = null;
      reject(new Error("Stripe.js did not load"));
    });
    document.head.appendChild(tag);
  });
  return pending;
}
